import {
  useRef,
  useState,
  type FormEvent,
} from 'react'
import {
  Eye,
  EyeOff,
  ShieldCheck,
} from 'lucide-react'
import { WolfMark } from '../components/brand/WolfMark'
import { useAuth } from '../context/AuthContext'

export function LoginPage() {
  const { login } = useAuth()

  const [username, setUsername] =
    useState('')

  const [passcode, setPasscode] =
    useState('')

  const [showPasscode, setShowPasscode] =
    useState(false)

  const [error, setError] =
    useState('')

  const [submitting, setSubmitting] =
    useState(false)

  const [
    passcodeError,
    setPasscodeError,
  ] = useState(false)

  const errorTimerRef =
    useRef<number | null>(null)

  function triggerPasscodeError() {
    if (errorTimerRef.current !== null) {
      window.clearTimeout(
        errorTimerRef.current,
      )
    }

    setPasscodeError(false)

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setPasscodeError(true)

        errorTimerRef.current =
          window.setTimeout(() => {
            setPasscodeError(false)
            errorTimerRef.current = null
          }, 520)
      })
    })
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault()

    setSubmitting(true)

    const accepted =
      await login(
        username,
        passcode,
      )

    if (!accepted) {
      setError(
        'The username or passcode is incorrect.',
      )

      triggerPasscodeError()

      setSubmitting(false)
      return
    }

    setError('')
    setPasscodeError(false)
    setSubmitting(false)
  }

  return (
    <main className="login-page">
      <section
        className="login-brand-panel"
        aria-label="William's Treasure introduction"
      >
        <div className="login-brand-lockup">
          <WolfMark
            className="login-wolf"
          />

          <div className="login-wordmark">
            <span className="login-wordmark-primary">
              WILLIAM'S
            </span>

            <span className="login-wordmark-gold">
              TREASURE
            </span>
          </div>
        </div>

        <div className="login-statement">
          <p>
            Built around
            <br />
            William Jones.
          </p>

          <span className="login-custom-copy">
            A custom CRM for William Jones,{' '}
            <em className="login-personal-highlight">
              a father of three, a husband,
              a leader at Wolf Forge, and
              part of the MJ Lopez team.
            </em>
          </span>
        </div>

        <div className="login-source-note">
          <ShieldCheck size={17} />
          Your CRM data stays protected
          behind secure access.
        </div>
      </section>

      <section className="login-form-panel">
        <form
          className="login-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="login-heading">
            <h1>
              Welcome back{' '}
              <em className="login-william">
                William
              </em>
            </h1>

            <p>
              Sign in to your private CRM
              workspace.
            </p>
          </div>

          <label
            className="field-label"
            htmlFor="username"
          >
            Username
          </label>

          <input
            autoComplete="username"
            className="text-input"
            id="username"
            name="username"
            onChange={(event) => {
              setUsername(event.target.value)
              setError('')
            }}
            placeholder="Enter your username"
            value={username}
          />

          <label
            className="field-label"
            htmlFor="passcode"
          >
            6-digit passcode
          </label>

          <div
            className={
              `passcode-field${
                passcodeError
                  ? ' is-error'
                  : ''
              }`
            }
          >
            <input
              autoComplete="current-password"
              className="text-input"
              id="passcode"
              inputMode="numeric"
              maxLength={6}
              name="passcode"
              onChange={(event) => {
                setPasscode(
                  event.target.value.replace(
                    /\D/g,
                    '',
                  ),
                )

                setError('')
                setPasscodeError(false)
              }}
              placeholder="Enter 6-digit passcode"
              type={
                showPasscode
                  ? 'text'
                  : 'password'
              }
              value={passcode}
            />

            <button
              className="passcode-toggle"
              onClick={() =>
                setShowPasscode(
                  (visible) => !visible,
                )
              }
              type="button"
              aria-label={
                showPasscode
                  ? 'Hide passcode'
                  : 'Show passcode'
              }
            >
              {showPasscode
                ? <EyeOff size={18} />
                : <Eye size={18} />}
            </button>
          </div>

          {error ? (
            <p
              className="form-error"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <button
            className="button button-primary login-button"
            disabled={submitting}
            type="submit"
          >
            {submitting
              ? 'Signing in...'
              : 'Enter workspace'}
          </button>

          <p className="login-security">
            Private access to
            William's Treasure
          </p>
        </form>
      </section>
    </main>
  )
}